import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { Observable, ReplaySubject, filter, startWith } from 'rxjs';
import { Breadcrumb, BreadcrumbDef, BreadcrumbEntry } from './breadcrumb.types';

/**
 * Builds the breadcrumb trail from the active route tree.
 *
 * Labels come from each route's resolved data via `data.breadcrumb`, not from
 * parsing the URL, so entity routes render real names instead of raw GUIDs.
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
    private _router = inject(Router);
    private _breadcrumbs = new ReplaySubject<Breadcrumb[]>(1);

    constructor() {
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                // Emit for the initial route too, since the first NavigationEnd
                // may already have fired before anything subscribes.
                startWith(null),
            )
            .subscribe(() => this._breadcrumbs.next(this.build()));
    }

    get breadcrumbs$(): Observable<Breadcrumb[]> {
        return this._breadcrumbs.asObservable();
    }

    /**
     * Build the trail for the currently active route.
     *
     * Public and synchronous so the title strategy can call it directly rather
     * than racing this service's own NavigationEnd subscription.
     */
    build(): Breadcrumb[] {
        const crumbs: Breadcrumb[] = [];
        let route: ActivatedRoute | null = this._router.routerState.root;
        let url = '';
        // Angular's `emptyOnly` inheritance doesn't copy an ancestor's params
        // (e.g. `:id`) into non-empty child routes, so accumulate them here to
        // let a breadcrumb function build links to ancestor list pages.
        let params: Params = {};

        while (route) {
            const snapshot = route.snapshot;
            params = { ...params, ...snapshot.params };

            // A route's path can span multiple segments (e.g. 'exhibitors/:id'),
            // so accumulate all of them to keep the URL accurate.
            const path = snapshot.url.map((segment) => segment.path).join('/');
            if (path) {
                url += '/' + path;
            }

            // Read the declaration from routeConfig rather than snapshot.data: Angular
            // inherits a parent's data into empty-path children, so `snapshot.data` would
            // report the parent's breadcrumb again on the child and duplicate the crumb.
            // The label is still resolved against snapshot.data, which is where resolvers
            // put their results.
            const def = route.routeConfig?.data?.['breadcrumb'];
            crumbs.push(...this._resolveCrumbs(def, snapshot.data, params, url));

            route = route.firstChild;
        }

        // The last crumb is the current page — not a link.
        if (crumbs.length > 0) {
            crumbs[crumbs.length - 1].url = null;
        }

        return crumbs;
    }

    /**
     * Resolve a `breadcrumb` definition to zero or more crumbs. Returns an empty
     * array when the route declares none, or when a breadcrumb function throws or
     * yields nothing — a bad crumb is dropped rather than allowed to break the
     * whole trail. A function may return an array to contribute several crumbs
     * from one route; a bare string element links to the route's own URL, an
     * object element can override the link target.
     */
    private _resolveCrumbs(
        def: BreadcrumbDef | undefined,
        data: any,
        params: Params,
        url: string,
    ): Breadcrumb[] {
        if (!def) {
            return [];
        }

        if (typeof def === 'string') {
            return [{ label: def, url }];
        }

        let result: string | BreadcrumbEntry[];
        try {
            result = def(data, params);
        } catch {
            return [];
        }

        const entries = Array.isArray(result) ? result : [result];

        return entries
            .map((entry): Breadcrumb | null => {
                if (typeof entry === 'string') {
                    return entry ? { label: entry, url } : null;
                }
                return entry?.label ? { label: entry.label, url: entry.url ?? url } : null;
            })
            .filter((crumb): crumb is Breadcrumb => crumb !== null);
    }
}
